import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import api from "../../api";
import insightsApi from "../../api/insightsApi";

const UNASSIGNED = "";

const toRows = (map) =>
    Object.entries(map || {}).map(([question, pillar]) => ({
        question,
        pillar: String(pillar || ""),
    }));

const fromRows = (rows) => {
    const map = {};
    (rows || []).forEach(({ question, pillar }) => { map[question] = String(pillar || ""); });
    return map;
};

/**
 * The confirmation step between pillar tagging and everything expensive.
 *
 * The run is parked: stage 2 tagged every open-text and Likert question with a
 * pillar and stopped. This shows that tagging, lets it be corrected, and
 * releases the rest of the pipeline — topics, report, signals.
 *
 * Why this and not topics: the pillar axis decides how the whole report is
 * grouped, it is one cheap LLM call to produce, and it is far cheaper to fix
 * here than after a report has been built on it. Topics are expensive to
 * compute, have their own editing flow once a report exists, and so run
 * unattended after this.
 */
const PillarTaggingReviewDialog = ({ open, insightId, configId, onClose, onConfirmed }) => {
    const [loading, setLoading] = useState(true);
    const [openTextRows, setOpenTextRows] = useState([]);
    const [likertRows, setLikertRows] = useState([]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // `submitting` only disables the button on the NEXT render, so a fast
    // double click can get two confirms out; the ref stops the second at once.
    const submittingRef = useRef(false);

    useEffect(() => {
        if (!open || !configId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const config = await api.getInsightConfig(configId);
                if (cancelled) return;
                const md = config?.metadata || {};
                setOpenTextRows(toRows(md.open_text_pillars));
                setLikertRows(toRows(md.likert_pillars));
            } catch (e) {
                if (!cancelled) setError("Could not load the pillar tagging.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, configId]);

    // Every pillar the tagging actually used. There is no separate vocabulary to
    // offer: the pillars are whatever this assessment was tagged against, so the
    // menu is built from the tagging itself.
    const options = useMemo(() => {
        const seen = new Set();
        const all = [];
        [...openTextRows, ...likertRows].forEach(({ pillar }) => {
            const name = String(pillar || "").trim();
            if (!name || seen.has(name.toLowerCase())) return;
            seen.add(name.toLowerCase());
            all.push(name);
        });
        return all.sort((a, b) => a.localeCompare(b));
    }, [openTextRows, likertRows]);

    const unassigned = [...openTextRows, ...likertRows]
        .filter((r) => !String(r.pillar || "").trim()).length;

    const setPillar = (setter) => (index, pillar) =>
        setter((prev) => prev.map((row, i) => (i === index ? { ...row, pillar } : row)));

    const handleConfirm = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setError(null);
        try {
            await insightsApi.confirmPillarTagging(insightId, {
                openTextPillars: fromRows(openTextRows),
                likertPillars: fromRows(likertRows),
            });
            if (onConfirmed) onConfirmed();
            if (onClose) onClose();
        } catch (e) {
            if (e?.response?.status === 409) {
                // Already confirmed (another click or tab got there first): the
                // run is going, so this dialog has nothing left to do.
                if (onConfirmed) onConfirmed();
                if (onClose) onClose();
                return;
            }
            setError(
                e?.response?.data?.error
                || "Could not start the rest of the run. Your changes were not lost — try again."
            );
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const renderSection = (title, rows, setter) => (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {title} ({rows.length})
            </Typography>
            <Stack spacing={1.5}>
                {rows.map((row, index) => (
                    <Stack
                        key={row.question}
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ md: "center" }}
                    >
                        <Typography variant="body2" sx={{ flex: 1 }}>{row.question}</Typography>
                        <TextField
                            select
                            size="small"
                            label="Pillar"
                            value={row.pillar}
                            onChange={(e) => setPillar(setter)(index, e.target.value)}
                            error={!row.pillar}
                            sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}
                        >
                            <MenuItem value={UNASSIGNED}><em>Not assigned</em></MenuItem>
                            {options.map((pillar) => (
                                <MenuItem key={pillar} value={pillar}>{pillar}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                ))}
                {!rows.length && (
                    <Typography variant="body2" color="text.secondary">
                        None in this assessment.
                    </Typography>
                )}
            </Stack>
        </Box>
    );

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Confirm the pillar tagging</DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Each question has been assigned to a pillar. Correct anything that
                            looks wrong — this is what the report is grouped by. Confirming
                            starts topic modelling and the report, which is the slow part.
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                            {options.map((p) => <Chip key={p} label={p} size="small" />)}
                        </Stack>
                        {unassigned > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {unassigned} question{unassigned === 1 ? "" : "s"} could not be
                                placed and will have no pillar in the report.
                            </Alert>
                        )}
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {renderSection("Open-text questions", openTextRows, setOpenTextRows)}
                        <Divider sx={{ mb: 3 }} />
                        {renderSection("Likert statements", likertRows, setLikertRows)}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Later</Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={loading || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {submitting ? "Starting…" : "Confirm and generate report"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PillarTaggingReviewDialog;
